import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Official Discord "Clyde" wordmark-free icon. From the Discord Brand Kit.
// Kept as inline SVG so we don't fetch a remote asset and don't rely on cache.
function DiscordIcon({ size = 16, colored = false }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={colored ? "#5865F2" : "currentColor"}
      aria-hidden="true"
    >
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.0768.0768 0 00-.0785-.037 19.7362 19.7362 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0313-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
    </svg>
  );
}

/**
 * Real numbers only, threshold-gated. Below the threshold we show a quiet
 * "quality" line instead of counters — no invented ratings, no fabricated
 * customer counts. See §4 of the Feb 2026 spec.
 */
export default function TrustStrip() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/trust-stats`)
      .then((r) => setStats(r.data))
      .catch(() => setStats({ show_counters: false, discord: { live: false, invite_url: "" } }));
  }, []);

  if (!stats) return <div className="h-8" aria-hidden="true" />;  // keeps layout stable

  const showCounters = stats.show_counters;

  return (
    <div
      className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] sm:text-[12px] text-ink-500 mb-4"
      data-testid="trust-strip"
    >
      {showCounters ? (
        <>
          <span data-testid="trust-items-priced">
            <strong className="text-ink-900 font-mono-num">{stats.items_priced.toLocaleString()}</strong>{" "}
            items priced
          </span>
          <span className="text-[#CBD5E1]" aria-hidden="true">·</span>
          <span data-testid="trust-resale-value">
            <strong className="text-ink-900 font-mono-num">${stats.resale_value_usd.toLocaleString()}</strong>{" "}
            in resale value found
          </span>
          <span className="text-[#CBD5E1]" aria-hidden="true">·</span>
        </>
      ) : (
        <>
          <span data-testid="trust-fallback">
            Every price backed by live market data
          </span>
          <span className="text-[#CBD5E1]" aria-hidden="true">·</span>
        </>
      )}
      <span data-testid="trust-free">2 free credits — scan + studio photo, no card</span>

      {/* Discord chip — config-driven soon/live state */}
      <DiscordChip live={stats.discord?.live} url={stats.discord?.invite_url} />
    </div>
  );
}

function DiscordChip({ live, url }) {
  const base =
    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#E9ECEF] text-[11px] font-medium transition-colors";
  if (live && url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="discord-chip-live"
        className={`${base} text-[#475569] hover:text-[#0F172A] hover:border-[#0F172A] bg-white`}
      >
        <DiscordIcon size={14} colored />
        Community
      </a>
    );
  }
  return (
    <span
      data-testid="discord-chip-soon"
      aria-disabled="true"
      className={`${base} text-[#94A3B8] bg-[#FAFBFC] cursor-not-allowed select-none`}
    >
      <DiscordIcon size={14} colored />
      Community
      <span className="ml-1 text-[9px] uppercase tracking-widest text-[#CBD5E1]">soon</span>
    </span>
  );
}
